import { ISavingsFixedBalances } from "app/calcs/retirementSavings"
import MaterialNumber from "app/components/material/MaterialNumber"
import {
    IQuestionsGroupContainerProps,
    QuestionsGroupContainer,
} from "app/containers/PipelineSection/QuestionGroupContainer"
import { getMemberContributionsChart } from "app/containers/PipelineSection/Retirement/charts"
import { IRetirementPerson, Profile } from "app/redux/Profile/model"
import { theCensusInfo } from "app/services/models/PublicCensusInfo"
import { toCurrency } from "app/utils"
import { getBrandHelpers } from "config"
import * as React from "react"
import { Col, Row } from "react-bootstrap"
import * as ReactHighcharts from "react-highcharts"
import moment = require("moment")

interface IDcAccounts extends ISavingsFixedBalances {
    chartConfig: Highcharts.Options
    personFirstNamePossessive: string
}

interface ILocal697MemberContributionsState extends IDcAccounts {
    allDcAccounts: string
    profile: Profile
}

class Local697MemberContributions extends QuestionsGroupContainer<
    IQuestionsGroupContainerProps,
    ILocal697MemberContributionsState
> {
    retirementSavingsNS = "retirement.{person}.fixedBalances.retirementSavings"
    fourxxBalanceNS = "retirement.{person}.fixedBalances.fourxxBalance"
    personalSavingsNS = "retirement.{person}.fixedBalances.personalSavings"
    healthReimbursementAccountNS =
        "retirement.{person}.fixedBalances.healthReimbursementAccount"

    state: ILocal697MemberContributionsState = {
        retirementSavings: this.getValueForQuestion(this.retirementSavingsNS),
        fourxxBalance: this.getValueForQuestion(this.fourxxBalanceNS),
        healthReimbursementAccount: this.getValueForQuestion(
            this.healthReimbursementAccountNS
        ),
        iraBalance: 0,
        personalSavings: this.getValueForQuestion(this.personalSavingsNS),
        allDcAccounts: "0",
        chartConfig: null,
        personFirstNamePossessive: "Member",
        profile: null,
    }

    person: IRetirementPerson
    personKind: string
    hraBalanceDate = moment(theCensusInfo.customInfo.HRABalanceDate)
    savingsDate = moment(theCensusInfo.customInfo.Local697SavingsDate)

    componentWillMount() {
        this.setup("member")
        this.updateDcTotal()

        this.onAppStateUpdate((appState) => {
            const profile = appState.Profile.getNative("info")
            const personFirstNamePossessive = "BertFarkle" //personPossessive(profile.getHeadOfHousehold().firstName)

            this.setState({ personFirstNamePossessive, profile })
        })
    }

    setup(personKind: string) {
        this.personKind = personKind
        this.retirementSavingsNS = this.retirementSavingsNS.replace(
            "{person}",
            "{" + personKind + "}"
        )
        this.healthReimbursementAccountNS =
            this.healthReimbursementAccountNS.replace(
                "{person}",
                "{" + personKind + "}"
            )
        this.fourxxBalanceNS = this.fourxxBalanceNS.replace(
            "{person}",
            "{" + personKind + "}"
        )
        this.personalSavingsNS = this.personalSavingsNS.replace(
            "{person}",
            "{" + personKind + "}"
        )

        this.state.retirementSavings = this.getValueForQuestion(
            this.retirementSavingsNS
        )
        this.state.healthReimbursementAccount = this.getValueForQuestion(
            this.healthReimbursementAccountNS
        )
        this.state.fourxxBalance = this.getValueForQuestion(
            this.fourxxBalanceNS
        )
        this.state.personalSavings = this.getValueForQuestion(
            this.personalSavingsNS
        )

        this.updateDcTotal()
    }

    update<K extends keyof ILocal697MemberContributionsState>(
        key: K,
        e: React.ChangeEvent<HTMLInputElement>,
        callback: () => any
    ) {
        const valueNum = this.toNumber(e.currentTarget.value)
        const newState = { ...this.state } as any
        newState[key] = valueNum

        // tslint:disable-next-line:no-debugger
        //debugger
        this.markSignificantProfileUpdate(this.state.profile)

        this.setState(newState, () => callback())
    }

    updateDC<B extends keyof IDcAccounts>(
        key: B,
        e: React.ChangeEvent<HTMLInputElement>
    ) {
        this.update(key, e, () => this.updateDcTotal())
    }

    updateDcTotal() {
        const {
            retirementSavings,
            fourxxBalance,
            healthReimbursementAccount,
            personalSavings,
        } = this.state
        let allDcAccountsNum = 0

        if (retirementSavings) {
            allDcAccountsNum += retirementSavings
        }
        if (fourxxBalance) {
            allDcAccountsNum += fourxxBalance
        }
        if (healthReimbursementAccount) {
            allDcAccountsNum += healthReimbursementAccount
        }
        if (personalSavings) {
            allDcAccountsNum += personalSavings
        }

        // Local 697 calls these differently
        const chartLabels = {
            fourxxBalance: "Other Retirement Savings",
        }
        const chartConfig = getMemberContributionsChart(
            chartLabels,
            retirementSavings,
            fourxxBalance,
            0,
            personalSavings,
            healthReimbursementAccount
        )
        let allDcAccounts = "0"

        if (!isNaN(allDcAccountsNum)) {
            allDcAccounts = toCurrency(allDcAccountsNum)
        }

        this.setState({ allDcAccounts, chartConfig })
    }

    renderMemberRetirementSavings() {
        if (!getBrandHelpers().hasIncomingSavingsBalance) {
            return null
        }
        if (this.personKind !== "member") {
            return null
        }
        const censusDateText = this.savingsDate.format("l")
        const brandInfo = this.getCustomerBrandInfo()
        const brandName =
            brandInfo.brandName +
            " DC Plan (as of " +
            censusDateText +
            ")<sup>*</sup>"
        const retirementSavingsLocked =
            this.getCustomerBrandHelpers().isLockedData(
                this.state.profile,
                ".fixedBalances.retirementSavings"
            )
        return (
            <div className="retirementSavings">
                <MaterialNumber
                    placeholder={brandName}
                    disabled={retirementSavingsLocked}
                    formattedNumberMode
                    name={this.retirementSavingsNS}
                    value={this.state.retirementSavings}
                    onChange={(e) => this.updateDC("retirementSavings", e)}
                />
            </div>
        )
    }

    render() {
        const hraBalanceHeading = "Local 697 Health Reimbursement Account"
        const local697 = this.state.profile.pension.local697
        const smaller = {
            fontSize: "0.84em",
        }
        return (
            <section className="retirementSection">
                <h1>Local 697's Savings Benefit</h1>
                <h3 className="lead retirementHeading">
                    How much have you saved for retirement?
                </h3>
                <Row>
                    <Col md={6}>
                        <br />
                        {this.renderMemberRetirementSavings()}

                        <MaterialNumber
                            placeholder={hraBalanceHeading}
                            formattedNumberMode
                            name={this.healthReimbursementAccountNS}
                            value={this.state.healthReimbursementAccount}
                            onChange={(e) =>
                                this.updateDC("healthReimbursementAccount", e)
                            }
                        />

                        <MaterialNumber
                            placeholder="Other Retirement Savings"
                            formattedNumberMode
                            name={this.fourxxBalanceNS}
                            value={this.state.fourxxBalance}
                            onChange={(e) => this.updateDC("fourxxBalance", e)}
                        />

                        <MaterialNumber
                            placeholder="Personal Savings"
                            formattedNumberMode
                            name={this.personalSavingsNS}
                            value={this.state.personalSavings}
                            onChange={(e) =>
                                this.updateDC("personalSavings", e)
                            }
                        />
                        <div>
                            Notes
                            <div style={smaller}>
                                <div>
                                    * IBEW Local 697 Defined Contribution Plan
                                    balance consists of the following:
                                </div>
                                <div>
                                    <span className="lt-green-txt">
                                        Pre-Tax Deferrals:&nbsp;
                                    </span>
                                    {toCurrency(local697.eePreTax)}
                                </div>
                                <div>
                                    <span className="lt-green-txt">
                                        After-Tax Contributions:&nbsp;
                                    </span>
                                    {toCurrency(local697.eeAfterTax)}
                                </div>
                                <div>
                                    <span className="lt-green-txt">
                                        Employer Contributions:&nbsp;
                                    </span>
                                    {toCurrency(local697.erContrib)}
                                </div>
                                <div>
                                    <span className="lt-green-txt">
                                        Rollovers:&nbsp;
                                    </span>
                                    {toCurrency(local697.rollover)}
                                </div>
                                <div>
                                    <span className="lt-green-txt">
                                        Roth deferrals:&nbsp;
                                    </span>
                                    {toCurrency(local697.rothDeferral)}
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col md={6} className="text-center">
                        <h2 className="animated fadeIn">
                            <article>
                                Current balance:&nbsp;
                                <a>{this.state.allDcAccounts}</a>
                            </article>
                        </h2>
                        <p>
                            <small>
                                (<b>Tip:</b> You can hover over data points to
                                get more info.)
                            </small>
                        </p>
                        <ReactHighcharts
                            className="animated fadeIn"
                            config={this.state.chartConfig}
                        />
                    </Col>
                </Row>
            </section>
        )
    }
}

const Local697MemberContributionsQuestionsGroup = () => (
    <Local697MemberContributions header="Contributions" icon="" />
)

export { Local697MemberContributionsQuestionsGroup }



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Retirement/Local697MemberSavings/index.tsx